import React from 'react'

import styles from './style.module.css'

export type DataTableColumn<T> = {
  key: string
  header: React.ReactNode
  width?: number // colgroup 用（px）。未指定は auto
  align?: 'left' | 'center' | 'right' // 既定 center
  headerStyle?: React.CSSProperties // ヘッダセル(th)へのインラインスタイル上書き（例: padding 調整）
  render: (row: T) => React.ReactNode
}

export type DataTableProps<T> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string | number
  onRowClick?: (row: T) => void
  emptyMessage?: React.ReactNode // 0件時に全列spanで中央表示
  maxHeight?: number | string // スクロール枠の高さ上限
}

const ALIGN_CLASS: Record<string, string> = {
  left: styles.alignLeft,
  center: styles.alignCenter,
  right: styles.alignRight,
}

export function DataTable<T>(props: DataTableProps<T>): JSX.Element {
  const { columns, rows, rowKey, onRowClick, emptyMessage, maxHeight } = props

  const wrapperStyle: React.CSSProperties = maxHeight != null ? { maxHeight } : {}

  const handleKeyDown = (row: T) => (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onRowClick?.(row)
    }
  }

  return (
    <div className={styles.wrapper} style={wrapperStyle}>
      <table className={styles.table}>
        <colgroup>
          {columns.map((col) => (
            <col key={col.key} style={col.width != null ? { width: col.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`${styles.th} ${ALIGN_CLASS[col.align ?? 'center'] ?? ''}`}
                style={col.headerStyle}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className={styles.emptyCell} colSpan={columns.length}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => {
              const key = rowKey(row)
              const isClickable = onRowClick != null

              return (
                <tr
                  key={key}
                  className={isClickable ? styles.rowClickable : undefined}
                  onClick={isClickable ? () => onRowClick(row) : undefined}
                  onKeyDown={isClickable ? handleKeyDown(row) : undefined}
                  tabIndex={isClickable ? 0 : undefined}
                  role={isClickable ? 'button' : undefined}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={`${styles.td} ${ALIGN_CLASS[col.align ?? 'center'] ?? ''}`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )
}
